import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { FiX, FiSearch, FiMapPin, FiFileText, FiCheckCircle, FiUsers, FiChevronRight, FiShield } from "react-icons/fi";
import { GoOrganization, GoCheck } from "react-icons/go";
import api from "../services/api";
import { SYSTEM_MODULES, SECTION_LABELS } from "../config/modules";
import { tokens, surface, hairline } from "../styles/designTokens";

const _mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const _eyebrow = css`
  ${_mono};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;
const _fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const _modalIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const _spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const STEP_CONFIG = [
  { id: 1, label: "Empresa", icon: GoOrganization },
  { id: 2, label: "Usuários", icon: FiUsers },
  { id: 3, label: "Permissões", icon: FiShield },
  { id: 4, label: "Revisão", icon: FiCheckCircle },
];
const TITLE_BY_STEP = {
  1: "Buscar Empresa",
  2: "Selecionar Usuários",
  3: "Módulos do Projeto",
  4: "Confirmar Criação",
};

const NewProjectModal = ({ isOpen, onClose, onConfirm }) => {
  const [step, setStep] = useState(1);
  const [cnpj, setCnpj] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // User selection state
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");

  // Permissions state
  const [permissions, setPermissions] = useState({});

  // Initialize permissions with false for all modules
  useEffect(() => {
    if (isOpen && Object.keys(permissions).length === 0) {
      const initialPermissions = {};
      SYSTEM_MODULES.forEach(module => {
        initialPermissions[module.id] = false;
      });
      setPermissions(initialPermissions);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setCnpj("");
      setCompanyData(null);
      setError("");
      setSelectedUsers([]);
      setUserSearchTerm("");
      setPermissions({});
    }
  }, [isOpen]);

  // Load users when step 2 is reached
  useEffect(() => {
    if (step === 2 && users.length === 0) {
      fetchUsers();
    }
  }, [step]);

  // Format CNPJ as user types
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 14) {
      return numbers
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return value;
  };

  const handleCnpjChange = (e) => {
    const formatted = formatCNPJ(e.target.value);
    setCnpj(formatted);
    setError("");
    setCompanyData(null);
  };

  const searchCNPJ = async () => {
    const cleanCnpj = cnpj.replace(/\D/g, "");
    
    if (cleanCnpj.length !== 14) {
      setError("CNPJ deve conter 14 dígitos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/cnpj/${cleanCnpj}`);

      if (response.data.success) {
        const data = response.data.data;
        
        setCompanyData({
          nome: data.nome || data.nomeFantasia,
          nomeFantasia: data.nomeFantasia,
          cnpj: cleanCnpj,
          cidade: data.cidade,
          uf: data.uf,
          situacao: data.situacao,
          atividade: data.atividadePrincipal,
        });
      } else {
        setError("CNPJ não encontrado ou inválido");
        setCompanyData(null);
      }
    } catch (err) {
      console.error("Erro ao buscar CNPJ:", err);
      
      // Verifica se é erro 409 (CNPJ já cadastrado)
      if (err.response?.status === 409) {
        const existingProject = err.response.data.existingProject;
        setError(`Este CNPJ já está cadastrado no projeto "${existingProject?.nome}"`);
      } else {
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            "CNPJ não encontrado ou inválido";
        setError(errorMessage);
      }
      
      setCompanyData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get("/users");
      const allUsers = response.data.users || [];
      
      // Filter out god users
      const filteredUsers = allUsers.filter(user => user.cargo !== "god");
      setUsers(filteredUsers);
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && cnpj.replace(/\D/g, "").length === 14) {
      searchCNPJ();
    }
  };

  const handleNext = () => {
    if (step === 1 && companyData) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const togglePermission = (moduleId) => {
    setPermissions(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleConfirm = () => {
    if (companyData) {
      onConfirm({
        ...companyData,
        selectedUsers: selectedUsers,
        permissions: permissions,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  const filteredUsers = users.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-top">
            <div className="title-block">
              <span className="eyebrow">PROJETOS · NOVO</span>
              <h2>{TITLE_BY_STEP[step]}</h2>
            </div>
            <CloseButton onClick={onClose}>
              <FiX />
            </CloseButton>
          </div>
          <div className="stepper">
            {STEP_CONFIG.map((s, i) => {
              const isActive = step === s.id;
              const isDone = step > s.id;
              const Icon = s.icon;
              return (
                <React.Fragment key={s.id}>
                  <div className={`step-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                    <div className="step-dot">
                      {isDone ? <GoCheck /> : <Icon />}
                    </div>
                    <span className="step-label">{s.label}</span>
                  </div>
                  {i < STEP_CONFIG.length - 1 && (
                    <div className={`step-line ${isDone ? 'done' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Step 1: CNPJ Search */}
          {step === 1 && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <GoOrganization />
                </div>
                <div className="info-content">
                  <h3>Adicionar Empresa ao Sistema</h3>
                  <p>
                    Digite o CNPJ da empresa para buscar automaticamente as
                    informações cadastrais e criar um novo projeto no sistema.
                  </p>
                </div>
              </InfoBox>

              <InputSection>
                <label>CNPJ da Empresa</label>
                <InputWrapper hasError={!!error}>
                  <input
                    type="text"
                    placeholder="00.000.000/0000-00"
                    value={cnpj}
                    onChange={handleCnpjChange}
                    onKeyPress={handleKeyPress}
                    maxLength={18}
                    autoFocus
                  />
                  <SearchButton
                    onClick={searchCNPJ}
                    disabled={loading || cnpj.replace(/\D/g, "").length !== 14}
                  >
                    {loading ? (
                      <div className="spinner" />
                    ) : (
                      <>
                        <FiSearch />
                        Buscar
                      </>
                    )}
                  </SearchButton>
                </InputWrapper>
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </InputSection>

              {companyData && (
                <CompanyPreview>
                  <PreviewHeader>
                    <div className="icon-wrapper">
                      <GoOrganization />
                    </div>
                    <div className="company-info">
                      <h3>{companyData.nome}</h3>
                      {companyData.nomeFantasia &&
                        companyData.nomeFantasia !== companyData.nome && (
                          <span className="fantasia">{companyData.nomeFantasia}</span>
                        )}
                    </div>
                    <div className="status-badge">
                      <FiCheckCircle />
                      {companyData.situacao}
                    </div>
                  </PreviewHeader>

                  <PreviewBody>
                    <InfoRow>
                      <div className="info-item">
                        <FiFileText />
                        <div>
                          <span className="label">CNPJ</span>
                          <span className="value">{formatCNPJ(companyData.cnpj)}</span>
                        </div>
                      </div>
                      <div className="info-item">
                        <FiMapPin />
                        <div>
                          <span className="label">Localização</span>
                          <span className="value">
                            {companyData.cidade} - {companyData.uf}
                          </span>
                        </div>
                      </div>
                    </InfoRow>

                    {companyData.atividade && (
                      <ActivityInfo>
                        <span className="label">Atividade Principal</span>
                        <span className="value">{companyData.atividade}</span>
                      </ActivityInfo>
                    )}
                  </PreviewBody>
                </CompanyPreview>
              )}
            </>
          )}

          {/* Step 2: User Selection */}
          {step === 2 && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiUsers />
                </div>
                <div className="info-content">
                  <h3>Selecionar Usuários (Opcional)</h3>
                  <p>
                    Escolha os usuários que terão acesso a este projeto. Você pode pular esta etapa e adicionar usuários depois.
                  </p>
                </div>
              </InfoBox>

              <InputSection>
                <label>Buscar Usuários</label>
                <SearchInput>
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                </SearchInput>
              </InputSection>

              <UsersList>
                {loadingUsers ? (
                  <LoadingMessage>Carregando usuários...</LoadingMessage>
                ) : filteredUsers.length === 0 ? (
                  <EmptyMessage>Nenhum usuário encontrado</EmptyMessage>
                ) : (
                  filteredUsers.map(user => (
                    <UserCard
                      key={user.id}
                      selected={selectedUsers.includes(user.id)}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="user-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <h4>{user.name}</h4>
                        <p>{user.email}</p>
                      </div>
                      <div className="user-role">
                        {user.cargo}
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <div className="check-icon">
                          <FiCheckCircle />
                        </div>
                      )}
                    </UserCard>
                  ))
                )}
              </UsersList>

              {selectedUsers.length > 0 && (
                <SelectedCount>
                  {selectedUsers.length} usuário{selectedUsers.length > 1 ? 's' : ''} selecionado{selectedUsers.length > 1 ? 's' : ''}
                </SelectedCount>
              )}
            </>
          )}

          {/* Step 3: Permissions */}
          {step === 3 && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiShield />
                </div>
                <div className="info-content">
                  <h3>Módulos Disponíveis no Projeto</h3>
                  <p>
                    Define quais módulos ficam acessíveis neste projeto. Usuários terão permissões individuais dentro destes limites.
                  </p>
                </div>
              </InfoBox>

              <ToggleHeader>
                <span>Módulo</span>
                <button
                  onClick={() => {
                    const allOn = SYSTEM_MODULES.every(m => permissions[m.id] === true);
                    const updated = {};
                    SYSTEM_MODULES.forEach(m => { updated[m.id] = !allOn; });
                    setPermissions(updated);
                  }}
                >
                  {SYSTEM_MODULES.every(m => permissions[m.id] === true) ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </ToggleHeader>

              <PermissionsList>
                {['dashboards', 'tools', 'settings'].map(section => {
                  const sectionModules = SYSTEM_MODULES.filter(m => m.section === section);
                  if (sectionModules.length === 0) return null;
                  return (
                    <PermissionSection key={section}>
                      <h5>{SECTION_LABELS[section]}</h5>
                      {sectionModules.map(module => {
                        const Icon = module.icon;
                        const isEnabled = permissions[module.id] === true;
                        return (
                          <ToggleItem
                            key={module.id}
                            $enabled={isEnabled}
                            $color={module.color}
                            onClick={() => togglePermission(module.id)}
                          >
                            <div className="toggle-label">
                              <Icon className="toggle-icon" />
                              <span>{module.label}</span>
                            </div>
                            <div className={`toggle-switch ${isEnabled ? 'on' : 'off'}`}>
                              <div className="toggle-knob" />
                            </div>
                          </ToggleItem>
                        );
                      })}
                    </PermissionSection>
                  );
                })}
              </PermissionsList>
            </>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <>
              <InfoBox>
                <div className="info-icon">
                  <FiCheckCircle />
                </div>
                <div className="info-content">
                  <h3>Confirmar Criação do Projeto</h3>
                  <p>
                    Revise as informações antes de criar o projeto.
                  </p>
                </div>
              </InfoBox>

              <SummarySection>
                <h3>Informações da Empresa</h3>
                <SummaryItem>
                  <span className="label">Nome:</span>
                  <span className="value">{companyData?.nome}</span>
                </SummaryItem>
                <SummaryItem>
                  <span className="label">CNPJ:</span>
                  <span className="value">{formatCNPJ(companyData?.cnpj)}</span>
                </SummaryItem>
                <SummaryItem>
                  <span className="label">Cidade:</span>
                  <span className="value">{companyData?.cidade} - {companyData?.uf}</span>
                </SummaryItem>
                
                <h3 style={{ marginTop: '20px' }}>Usuários Selecionados</h3>
                {selectedUsers.length === 0 ? (
                  <EmptyMessage>Nenhum usuário selecionado</EmptyMessage>
                ) : (
                  <SelectedUsersList>
                    {selectedUsers.map(userId => {
                      const user = users.find(u => u.id === userId);
                      return user ? (
                        <SelectedUserItem key={userId}>
                          <div className="avatar">{user.name?.charAt(0).toUpperCase()}</div>
                          <div className="info">
                            <span className="name">{user.name}</span>
                            <span className="email">{user.email}</span>
                          </div>
                        </SelectedUserItem>
                      ) : null;
                    })}
                  </SelectedUsersList>
                )}

                <h3 style={{ marginTop: '20px' }}>Módulos Ativados</h3>
                {SYSTEM_MODULES.filter(m => permissions[m.id] === true).length === 0 ? (
                  <EmptyMessage>Nenhum módulo ativado</EmptyMessage>
                ) : (
                  <PermissionsSummary>
                    {SYSTEM_MODULES.filter(m => permissions[m.id] === true).map(module => (
                      <PermissionSummaryItem key={module.id}>
                        <span className="module">{module.label}</span>
                        <span className="level">Ativo</span>
                      </PermissionSummaryItem>
                    ))}
                  </PermissionsSummary>
                )}
              </SummarySection>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <div className="footer-left">
            {step > 1 && (
              <BackButton onClick={handleBack}>
                Voltar
              </BackButton>
            )}
          </div>
          <div className="footer-right">
            <CancelButton onClick={onClose}>Cancelar</CancelButton>
            {step < 4 ? (
              <NextButton onClick={handleNext} disabled={step === 1 && !companyData}>
                Próximo
                <FiChevronRight />
              </NextButton>
            ) : (
              <ConfirmButton onClick={handleConfirm}>
                Criar Projeto
              </ConfirmButton>
            )}
          </div>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2em;
  animation: ${_fadeIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 580px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${_modalIn} ${tokens.durBase} ${tokens.easeOut};
`;

const ModalHeader = styled.div`
  padding: 16px 20px 14px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .header-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
  }

  .title-block {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .eyebrow {
      ${_eyebrow};
      font-size: 0.58em;
      opacity: 0.4;
    }

    h2 {
      margin: 2px 0 0 0;
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
      line-height: 1.2;
    }
  }

  /* STEPPER */
  .stepper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 2px 0;

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;

      .step-dot {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.85em; }
      }

      .step-label {
        ${_mono};
        font-size: 0.55em;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: opacity ${tokens.durBase} ${tokens.easeOut};
      }

      &.active {
        .step-dot {
          background: ${tokens.brandSoft};
          border-color: ${tokens.brand};
          color: ${tokens.brand};
          box-shadow: 0 0 0 3px ${tokens.brandRing};
          opacity: 1;
        }
        .step-label { color: ${tokens.brand}; opacity: 1; }
      }

      &.done {
        .step-dot {
          background: ${tokens.brand};
          border-color: ${tokens.brand};
          color: white;
          opacity: 1;
        }
        .step-label { opacity: 0.7; }
      }
    }

    .step-line {
      flex: 1;
      height: 1px;
      background: ${({ theme }) => hairline(theme)};
      margin-top: -14px;
      transition: background ${tokens.durBase} ${tokens.easeOut};

      &.done { background: ${tokens.brand}; opacity: 0.5; }
    }
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  color: ${({ theme }) => theme.text};
  opacity: 0.65;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokens.radiusMd};
  transition: all ${tokens.durBase} ${tokens.easeOut};
  flex-shrink: 0;

  svg { font-size: 1em; }

  &:hover {
    opacity: 1;
    border-color: ${({ theme }) => hairline(theme, true)};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 18px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: ${tokens.brandSoft};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusMd};

  .info-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${tokens.brandRing};
    border-radius: 50%;
    color: ${tokens.brand};
    font-size: 0.95em;
    flex-shrink: 0;
  }

  .info-content {
    flex: 1;

    h3 {
      font-size: 0.95em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      margin: 0 0 6px 0;
    }

    p {
      font-size: 0.85em;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      margin: 0;
      line-height: 1.5;
    }
  }
`;

const InputSection = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    font-size: 0.9em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin-bottom: 10px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 12px;

  input {
    flex: 1;
    padding: 14px 16px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 2px solid ${({ hasError, theme }) => hasError ? '#ef4444' : theme.border};
    border-radius: 10px;
    color: ${({ theme }) => theme.text};
    font-size: 1em;
    font-family: "SF Mono", "Monaco", monospace;
    font-weight: 500;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${({ hasError }) => hasError ? '#ef4444' : '#10b981'};
      background: ${({ theme }) => surface(theme, 0)};
    }

    &::placeholder {
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
    }
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  background: #10b981;
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg {
    font-size: 1.1em;
  }

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  margin-top: 8px;
  padding: 10px 14px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 0.85em;
  font-weight: 500;
`;

const CompanyPreview = styled.div`
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px solid ${({ theme }) => hairline(theme)};
  border-radius: 14px;
  overflow: hidden;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px;
  background: ${({ theme }) => surface(theme, 1)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .icon-wrapper {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 14px;
    color: white;
    font-size: 1.6em;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .company-info {
    flex: 1;
    min-width: 0;

    h3 {
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      margin: 0 0 4px 0;
      line-height: 1.3;
    }

    .fantasia {
      display: block;
      font-size: 0.85em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-weight: 500;
    }
  }

  .status-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 8px;
    color: #10b981;
    font-size: 0.75em;
    font-weight: 600;
    white-space: nowrap;

    svg {
      font-size: 1.1em;
    }
  }
`;

const PreviewBody = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  .info-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 10px;

    svg {
      font-size: 1.2em;
      color: #10b981;
      margin-top: 2px;
      flex-shrink: 0;
    }

    div {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      .label {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .value {
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        font-weight: 600;
        font-family: "SF Mono", "Monaco", monospace;
      }
    }
  }
`;

const ActivityInfo = styled.div`
  padding: 14px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: 10px;

  .label {
    display: block;
    font-size: 0.75em;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }

  .value {
    display: block;
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    line-height: 1.5;
  }
`;

const ModalFooter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => surface(theme, 0)};

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }

  gap: 8px;
  padding: 14px 22px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .footer-left, .footer-right {
    display: flex;
    gap: 8px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  background: transparent;
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  opacity: 0.85;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
  }
`;

const NextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: ${tokens.brand};
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  color: white;
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  svg { font-size: 0.95em; }

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  background: transparent;
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  color: ${({ theme }) => theme.text};
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  opacity: 0.85;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
  }
`;

const ConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: ${tokens.brand};
  border: 1px solid ${tokens.brand};
  border-radius: ${tokens.radiusMd};
  color: white;
  font-family: ${tokens.fontSans};
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};

  &:hover:not(:disabled) {
    background: ${tokens.brandHover};
    border-color: ${tokens.brandHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Step 2 components
const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ theme }) => surface(theme, 1)};
  border: 2px solid ${({ theme }) => hairline(theme)};
  border-radius: 10px;
  transition: all 0.2s ease;

  svg {
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 1.1em;
  }

  input {
    flex: 1;
    background: none;
    border: none;
    color: ${({ theme }) => theme.text};
    font-size: 0.9em;
    outline: none;

    &::placeholder {
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
    }
  }

  &:focus-within {
    border-color: #10b981;
  }
`;

const UsersList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-top: 16px;
  margin-right: -28px;
  padding-right: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => surface(theme, 1)};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme)};
    border-radius: 10px;

    &:hover {
      background: #10b981;
    }
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 2px solid ${({ selected, theme }) => selected ? '#10b981' : theme.border};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #10b981;
    background: ${({ theme }) => surface(theme, 1)};
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ selected }) => selected ? '#10b981' : '#6b7280'};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9em;
    flex-shrink: 0;
  }

  .user-info {
    flex: 1;
    min-width: 0;

    h4 {
      margin: 0 0 4px 0;
      font-size: 0.9em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    p {
      margin: 0;
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }
  }

  .user-role {
    padding: 4px 10px;
    background: ${({ theme }) => surface(theme, 1)};
    border-radius: 6px;
    font-size: 0.75em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    opacity: 0.8;
    text-transform: capitalize;
  }

  .check-icon {
    color: #10b981;
    font-size: 1.3em;
    display: flex;
    align-items: center;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  font-size: 0.9em;
`;

const SelectedCount = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  color: #10b981;
  font-size: 0.85em;
  font-weight: 600;
  text-align: center;
`;

// Step 3 components
const SummarySection = styled.div`
  h3 {
    font-size: 0.95em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    margin: 0 0 12px 0;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: 8px;
  margin-bottom: 8px;

  .label {
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    font-weight: 600;
  }

  .value {
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    font-weight: 600;
  }
`;

const SelectedUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SelectedUserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: 8px;

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #10b981;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.85em;
  }

  .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .name {
      font-size: 0.85em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    .email {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }
  }
`;

// Step 3 - Permissions components (exact match from users screen)
const PermissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  margin-top: 16px;
  max-height: 450px;
  overflow-y: auto;
  margin-right: -28px;
  padding-right: 28px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme)};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.text}40;
    }
  }
`;

const PermissionSection = styled.div`
  h5 {
    margin: 0 0 1.2em 0;
    font-size: 0.75em;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.5;
  }
`;

const ToggleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  span {
    font-size: 0.85em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  button {
    background: none;
    border: 1px solid ${({ theme }) => hairline(theme)};
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 0.8em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      opacity: 1;
      border-color: #10b981;
      color: #10b981;
    }
  }
`;

const ToggleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: ${({ $enabled, $color }) => $enabled ? `${$color}08` : 'transparent'};
  border: 1px solid ${({ $enabled, $color, theme }) => $enabled ? `${$color}30` : theme.border};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child { margin-bottom: 0; }

  &:hover {
    border-color: ${({ $color }) => $color || '#10b981'}60;
    background: ${({ $color }) => $color || '#10b981'}08;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 12px;

    .toggle-icon {
      font-size: 18px;
      color: ${({ $enabled, $color }) => $enabled ? ($color || '#10b981') : '#6b7280'};
      transition: color 0.2s;
    }

    span {
      font-size: 0.9em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }
  }

  .toggle-switch {
    width: 42px;
    height: 24px;
    border-radius: 12px;
    position: relative;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &.on {
      background: ${({ $color }) => $color || '#10b981'};
    }
    &.off {
      background: ${({ theme }) => hairline(theme)};
    }

    .toggle-knob {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: white;
      position: absolute;
      top: 3px;
      transition: all 0.2s ease;
      box-shadow: 0 1px 3px rgba(0,0,0,0.15);
    }

    &.on .toggle-knob { left: 21px; }
    &.off .toggle-knob { left: 3px; }
  }
`;

const PermissionsSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PermissionSummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: 8px;

  .module {
    font-size: 0.85em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  .level {
    font-size: 0.75em;
    font-weight: 600;
    padding: 4px 10px;
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 6px;
    color: #10b981;
  }
`;

export default NewProjectModal;
